import { useState } from "react";
import { useTranslation } from "@ncp/i18n";
import { ValidationReport } from "@ncp/page-host";
import { InlineAlert, Skeleton, notifyDone, toast } from "@ncp/ui";
import { usePageActions, usePageScreenshots, usePageValidation, type PageDetail } from "../api";

/** The checks a version must pass before release: quick on every save, full (build, render with axe in both languages, agent evals) on demand. */
export function ValidationTab({ detail, canRun, onOpenIssue, onChecked }: { detail: PageDetail; canRun: boolean; onOpenIssue: (path: string, line?: number | null) => void; onChecked: () => void }) {
  const { t } = useTranslation();
  const id = detail.page.id;
  const state = usePageValidation(id);
  const shots = usePageScreenshots(id);
  const actions = usePageActions();
  const [running, setRunning] = useState<"quick" | "full" | null>(null);

  const run = async (mode: "quick" | "full") => {
    setRunning(mode);
    try {
      const r = await actions.validate.mutateAsync({ id, mode });
      if (r.status === "fail") toast.error(t("admin:pageStudio.validation.failed", { count: r.errors }));
      else notifyDone(r.status === "warn" ? t("admin:pageStudio.validation.warnings", { count: r.warnings }) : t("admin:pageStudio.validation.passed"));
      await Promise.all([state.refetch(), shots.refetch()]);
      onChecked();
    } catch { /* useSave reported it */ } finally { setRunning(null); }
  };

  if (state.isLoading) return <Skeleton className="h-64" />;
  return (
    <div className="space-y-3">
      {detail.issues.length > 0 ? (
        <InlineAlert tone="danger" title={t("admin:pageStudio.validation.projectIssues")}>
          <ul className="list-disc ps-4">{detail.issues.map((i, k) => <li key={k}><code dir="ltr">{i.path}</code>: {i.error}</li>)}</ul>
        </InlineAlert>
      ) : null}
      <ValidationReport report={state.data?.report ?? detail.validation} stale={state.data?.stale} screenshots={shots.data} running={running} onRun={canRun ? (m) => void run(m) : undefined} onOpenIssue={onOpenIssue} />
    </div>
  );
}
